import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock } from "vue"

import type { PropType } from 'vue';
import { ref, computed, toRefs, reactive, watch } from 'vue';
import { useQuasar } from 'quasar';

// PERSONAL COMPONENTS
import QBtnCancel from '@components/form/QBtnCancel.vue';
import QBtnValid from '@components/form/QBtnValid.vue';
import QDialogTemplate from '@components/template/QDialogTemplate.vue';

// ViewModel
import { useGlobalVM } from '@viewmodel/global.vm';

// PERSONAL LIBS
import adminRepository from '@data/repository/admin.repository';
import { IUser } from '@shared/interface/model.interface';
import { $t } from 'src/i18n/utils';
import { RoleEnum } from '@shared/enum/role.enum';
import {
  displayDialogError,
  displayDialogWarning,
  displayNotifSuccess,
} from '@shared/lib/notify.lib';
import { UserDto } from '@data/dto/user-dto';
import { IApiResponse } from '@shared/interface/api-response.interface';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';

interface IRole {
  code: RoleEnum;
  description: string;
  checked: boolean;
}

/**
 * Events
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'DialogUserRoles',
  props: {
  show: { type: Boolean, required: true, default: false },
  user: { type: Object as PropType<IUser>, required: true },
},
  emits: ['close', 'success', 'failed'],
  setup(__props, { emit: __emit }) {

// VENDOR
const emits = __emit;

/**
 * Globals
 */
const $q = useQuasar()

/**
 * Viewmodel
 */
const { isMobile } = useGlobalVM();

/**
 * Datas
 */
let backupRoles: IRole[] = [];

/**
 * Reactive datas
 */
const loading = ref<boolean>(false);
const roles = reactive<IRole[]>([]);
roles.push({
  code: RoleEnum.ROLE_SUPER_ADMIN,
  description: $t(`users.roles.${RoleEnum.ROLE_SUPER_ADMIN}`),
  checked: false,
});
roles.push({
  code: RoleEnum.ROLE_ADMIN,
  description: $t(`users.roles.${RoleEnum.ROLE_ADMIN}`),
  checked: false,
});
roles.push({
  code: RoleEnum.ROLE_DEV,
  description: $t(`users.roles.${RoleEnum.ROLE_DEV}`),
  checked: false,
});
roles.push({
  code: RoleEnum.ROLE_USER_VALIDATE,
  description: $t(`users.roles.${RoleEnum.ROLE_USER_VALIDATE}`),
  checked: false,
});
roles.push({
  code: RoleEnum.ROLE_VIDEO_VALIDATE,
  description: $t(`users.roles.${RoleEnum.ROLE_VIDEO_VALIDATE}`),
  checked: false,
});
roles.push({
  code: RoleEnum.ROLE_VIDEO_ADD,
  description: $t(`users.roles.${RoleEnum.ROLE_VIDEO_ADD}`),
  checked: false,
});
roles.push({
  code: RoleEnum.ROLE_PRG_EDIT,
  description: $t(`users.roles.${RoleEnum.ROLE_PRG_EDIT}`),
  checked: false,
});

/**
 * Computed properties
 */
const connectedUser = computed<IUser | undefined>(
  () => sessionManager.currentUser.value,
);
const disableSuperAdminRole = computed<boolean>(() => {
  return (
    (connectedUser.value?.roles != null &&
      connectedUser.value.roles.includes(RoleEnum.ROLE_SUPER_ADMIN) &&
      user.value.email === connectedUser.value.email) ||
    (connectedUser.value?.roles != null &&
      !connectedUser.value.roles.includes(RoleEnum.ROLE_SUPER_ADMIN))
  );
});
const disableDevRole = computed<boolean>(() => {
  return (
    (connectedUser.value?.roles != null &&
      !connectedUser.value.roles.includes(RoleEnum.ROLE_SUPER_ADMIN))
  );
});
const updatesMade = computed<boolean>(() => {
  let _updatesMade = false;

  roles.forEach((role) => {
    const find = backupRoles.find((r) => r.code === role.code);
    if (find != null && find.checked !== role.checked) {
      _updatesMade = true;
    }
  });

  return _updatesMade;
});

/**
 * Properties
 */
const props = __props;
const { user, show } = toRefs(props);

/**
 * Methods
 */
function hasToDisable(role: IRole): boolean {
  return (
    loading.value ||
    (role.code === RoleEnum.ROLE_SUPER_ADMIN && disableSuperAdminRole.value) ||
    (role.code === RoleEnum.ROLE_DEV && disableDevRole.value)
  );
}

function onClose() {
  emits('close');
}

function onValid() {
  loading.value = true;

  const currentUser = new UserDto(user.value);
  currentUser.roles = roles.filter((r) => r.checked).map((r) => r.code);

  currentUser.roles.push(RoleEnum.ROLE_READ);

  adminRepository
    .updateUserRoles(currentUser)
    .then((response: IApiResponse<string, string> | undefined) => {
      if (response?.returnCode === 'ADMIN_UPDATE_USER_OK') {
        displayNotifSuccess(
          `Les droits de l'utilisateur '${user.value.username}' ont été modifiés avec succès.`,
        );
      } else if (response?.returnCode === 'ADMIN_UPDATE_USER_OK_NO_MAIL') {
        displayDialogWarning({
          message: `Les droits de l'utilisateur '${user.value.username}' ont été modifiés avec succès (mais il y a eu une erreur lors de l'envoi du mail à l'utilisateur).`,
        });
      }

      updateBackupRoles();

      emits('success');
      emits('close');
    })
    .catch((error) => {
      displayDialogError({
        message: error.message ?? 'null',
      });
      emits('failed');
    })
    .finally(() => {
      loading.value = false;
    });
}

function updateBackupRoles() {
  backupRoles = [];
  roles.forEach((role) => backupRoles.push(Object.assign({}, role)));
}

/**
 * Watch properties
 */
watch(
  () => show.value,
  (newVal: boolean) => {
    if (newVal === true) {
      for (const role of roles) {
        if (user.value.roles?.includes(role.code)) {
          role.checked = true;
        } else {
          role.checked = false;
        }
      }

      // Backup roles (to check if update were made)
      updateBackupRoles();
    }
  },
  { immediate: true },
);

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_item_label = _resolveComponent("q-item-label")!
  const _component_q_checkbox = _resolveComponent("q-checkbox")!
  const _component_q_item_section = _resolveComponent("q-item-section")!
  const _component_q_item = _resolveComponent("q-item")!
  const _component_q_list = _resolveComponent("q-list")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!

  return (_openBlock(), _createBlock(QDialogTemplate, {
    "show-dialog": _unref(show),
    title: "Profil et rôles",
    maximized: _unref(isMobile),
    persistent: loading.value,
    "disable-close-button": loading.value,
    onClose: _cache[2] || (_cache[2] = ($event: any) => (onClose()))
  }, {
    content: _withCtx(() => [
      _createVNode(_component_q_card, {
        flat: "",
        class: "no-border-radius"
      }, {
        default: _withCtx(() => [
          _createCommentVNode(" Profile "),
          _createVNode(_component_q_card_section, {
            horizontal: "",
            class: "q-ma-sm q-pl-sm q-py-xs"
          }, {
            default: _withCtx(() => [
              _createTextVNode(" Utilisateur : " + _toDisplayString(_unref(user).username) + " (" + _toDisplayString(_unref(user).email) + ") ", 1 /* TEXT */)
            ]),
            _: 1 /* STABLE */
          }),
          _createVNode(_component_q_separator, { role: "separator" }),
          _createCommentVNode(" Rôles "),
          _createVNode(_component_q_card_section, {
            horizontal: "",
            class: "q-pb-sm"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_list, {
                role: "group",
                "aria-label": "Liste des rôles à sélectionner pour cet utilisateur"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_q_item_label, {
                    header: "",
                    class: _normalizeClass({
                'text-white': _unref($q).dark.isActive,
                'text-black': !_unref($q).dark.isActive,
                'q-pb-sm q-mb-none': true
              })
                  }, {
                    default: _withCtx(() => _cache[3] || (_cache[3] = [
                      _createTextVNode(" Rôles : ")
                    ])),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["class"]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(roles, (role, index) => {
                    return (_openBlock(), _createBlock(_component_q_item, {
                      key: index,
                      tag: "label",
                      tabindex: "-1",
                      disable: hasToDisable(role)
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_q_item_section, {
                          side: "",
                          top: ""
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_q_checkbox, {
                              "aria-labelledby": role.code,
                              modelValue: role.checked,
                              "onUpdate:modelValue": ($event: any) => ((role.checked) = $event),
                              class: "set-outline",
                              disable: hasToDisable(role)
                            }, null, 8 /* PROPS */, ["aria-labelledby", "modelValue", "onUpdate:modelValue", "disable"])
                          ]),
                          _: 2 /* DYNAMIC */
                        }, 1024 /* DYNAMIC_SLOTS */),
                        _createVNode(_component_q_item_section, {
                          id: role.code
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_q_item_label, null, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(role.code), 1 /* TEXT */)
                              ]),
                              _: 2 /* DYNAMIC */
                            }, 1024 /* DYNAMIC_SLOTS */),
                            _createVNode(_component_q_item_label, null, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(role.description), 1 /* TEXT */)
                              ]),
                              _: 2 /* DYNAMIC */
                            }, 1024 /* DYNAMIC_SLOTS */)
                          ]),
                          _: 2 /* DYNAMIC */
                        }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["id"])
                      ]),
                      _: 2 /* DYNAMIC */
                    }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["disable"]))
                  }), 128 /* KEYED_FRAGMENT */))
                ]),
                _: 1 /* STABLE */
              })
            ]),
            _: 1 /* STABLE */
          })
        ]),
        _: 1 /* STABLE */
      })
    ]),
    footer: _withCtx(() => [
      _createVNode(_component_q_card, {
        flat: "",
        class: "no-border-radius"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_q_separator),
          _createVNode(_component_q_card_actions, { align: "right" }, {
            default: _withCtx(() => [
              _createVNode(QBtnCancel, {
                label: 'Annuler',
                onClick: _cache[0] || (_cache[0] = ($event: any) => (onClose())),
                disable: loading.value
              }, null, 8 /* PROPS */, ["disable"]),
              _createVNode(QBtnValid, {
                label: 'Modifier',
                onClick: _cache[1] || (_cache[1] = ($event: any) => (onValid())),
                disable: loading.value || !updatesMade.value,
                loading: loading.value,
                "loading-label": '',
                title: !updatesMade.value ? 'Aucune modification détectée' : undefined
              }, null, 8 /* PROPS */, ["disable", "loading", "title"])
            ]),
            _: 1 /* STABLE */
          })
        ]),
        _: 1 /* STABLE */
      })
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["show-dialog", "maximized", "persistent", "disable-close-button"]))
}
}

})